/* Copyright (c) 2021-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Injectable } from '@angular/core';
import { from, Observable, of } from 'rxjs';
import { map, shareReplay, startWith } from 'rxjs/operators';
import {
    PlatformDetails,
    getPlatformDetails,
    PlatformName,
    IsFeatureSupported,
    BrowserFeature,
    translatePlatformDetailsForPayload,
    translatePlatformDetailsForClientHeader,
    PayloadOSType,
    PayloadBrowserType,
    PayloadDeviceType,
    ClientHeaderOSType,
    ClientHeaderBrowserType,
    ClientHeaderDeviceType,
    getNetworkTypeDetector,
    NetworkType,
    NetworkUpdateCallback,
    NetworkTypeDetector
} from '@gamestream/ragnarok';
import { Logger, LoggingService } from 'crimson/lib/logging/logging.service';
import { PlatformDetectorService } from 'crimson/lib/platform-detector/platform-detector.service';
import { getTizenCapability, getTizenChipsetName, TizenDeviceCapabilityKey } from 'utils/lib/platform/tizen';

/**
 * PlatformInfo extends PlatformDetails from Ragnarok to include more data if available.
 */
export interface PlatformInfo {
    details: PlatformDetails;
    systemName?: string;
    processorArchitecture?: string;
    cpuName?: string;
}

/**
 * WARNING: This Service is only meant to be used from browser plugins.
 * DO NOT USE THIS FROM CLIENT UI CODE.
 */
@Injectable({
    providedIn: 'root'
})
export class PlatformInfoService {
    private logger: Logger;
    private platformInfo$: Observable<PlatformInfo>;
    private _networkTypeDetector: NetworkTypeDetector;

    private get networkTypeDetector() {
        if (!this._networkTypeDetector) {
            this._networkTypeDetector = getNetworkTypeDetector();
        }
        return this._networkTypeDetector;
    }

    constructor(loggingService: LoggingService) {
        this.logger = loggingService.getLogger('PlatformInfoService');
        this.platformInfo$ = from(getPlatformDetails()).pipe(
            map(platformDetails => {
                const platformName = platformDetails.os;
                const platformInfo: PlatformInfo = {
                    details: platformDetails,
                    systemName: this.getSystemName(platformDetails),
                    processorArchitecture: this.getProcessorArchitecture(platformName),
                    cpuName: this.getCPUName(platformName)
                };
                return platformInfo;
            }),
            shareReplay(1)
        );
    }

    /**
     * WARNING: This Service is only meant to be used from browser plugins.
     * DO NOT USE THIS FROM CLIENT UI CODE.
     */
    getPlatformInfo(): Observable<PlatformInfo> {
        if (!PlatformDetectorService.isBrowserPlatform()) {
            return of(null);
        }
        return this.platformInfo$;
    }

    /**
     * Use Ragnarok's Network Detector to get current network type it detects.
     *
     * @returns Ragnarok's NetworkType enum
     */
    getNetworkType(): NetworkType {
        return this.networkTypeDetector.getCurrentNetworkType();
    }

    /**
     * Use Ragnarok's Network Detector to listen network changes.
     *
     * @returns Ragnarok's NetworkType enum
     */
    onNetworkTypeChanged(): Observable<NetworkType> {
        const networkTypeChanged$ = new Observable<NetworkType>(subscriber => {
            // A flag to prevent emitting value when callbacks from Ragnarok after subscriber unsubscribes.
            let subscribe = true;
            // Ideally, should only listen the callback when supported but Ragnarok has trouble to accurately check
            // if network detection is supported on different platforms (GSRTC-2692).
            const callback: NetworkUpdateCallback = (networkType: NetworkType) => {
                if (subscribe) {
                    subscriber.next(networkType);
                }
            };
            this.networkTypeDetector.registerCallback(callback);
            // unsubscribe callback by subscribers
            return () => {
                subscribe = false;
                this.networkTypeDetector.unregisterCallback(callback);
            };
        });
        // To prevent the callback from not being called by Ragnarok,
        // emit current network type value by using the synchronous call in Ragnarok.
        return networkTypeChanged$.pipe(startWith(this.getNetworkType()));
    }

    isBrowserStreamingSupported(): Observable<boolean> {
        return this.platformInfo$.pipe(
            map((platformInfo: PlatformInfo) => IsFeatureSupported(BrowserFeature.Streaming, platformInfo.details))
        );
    }

    getTranslatedPayloadDeviceType(platformDetails: PlatformDetails): PayloadDeviceType {
        return translatePlatformDetailsForPayload(platformDetails).deviceType;
    }

    getTranslatedPayloadOsType(platformDetails: PlatformDetails): PayloadOSType {
        return translatePlatformDetailsForPayload(platformDetails).os;
    }

    getTranslatedPayloadBrowserType(platformDetails: PlatformDetails): PayloadBrowserType {
        return translatePlatformDetailsForPayload(platformDetails).browser;
    }

    getTranslatedClientHeaderDeviceType(platformDetails: PlatformDetails): ClientHeaderDeviceType {
        return translatePlatformDetailsForClientHeader(platformDetails).deviceType;
    }

    getTranslatedClientHeaderOsType(platformDetails: PlatformDetails): ClientHeaderOSType {
        return translatePlatformDetailsForClientHeader(platformDetails).os;
    }

    getTranslatedClientHeaderBrowserType(platformDetails: PlatformDetails): ClientHeaderBrowserType {
        return translatePlatformDetailsForClientHeader(platformDetails).browser;
    }

    private getSystemName(platformDetails: PlatformDetails): string {
        if (platformDetails.os === PlatformName.TIZEN) {
            return this.handleTizenCapability(TizenDeviceCapabilityKey.ModelName);
        }
        return platformDetails.deviceModel || undefined;
    }

    private getProcessorArchitecture(platformName: PlatformName): string {
        if (platformName === PlatformName.TIZEN) {
            return this.handleTizenCapability(TizenDeviceCapabilityKey.PlatformCoreCpuArch);
        }
        const AppVersion = window.navigator.appVersion;
        let processorArchitecture = 'x86';
        if (AppVersion.includes('WOW64') || AppVersion.includes('Win64')) {
            processorArchitecture = 'x64';
        } else if (AppVersion.includes('arm')) {
            processorArchitecture = 'arm';
        }
        return processorArchitecture;
    }

    private getCPUName(platformName: PlatformName): string {
        if (platformName !== PlatformName.TIZEN) {
            return undefined;
        }
        return getTizenChipsetName();
    }

    private handleTizenCapability(key: TizenDeviceCapabilityKey): string {
        try {
            return getTizenCapability(key);
        } catch (err) {
            this.logger.error(`Failed to get Tizen capability ${key}: ${err}`);
            return undefined;
        }
    }
}
